"use client";

import { useMemo, useState } from "react";
import { Download as DownloadIcon, File as FileIcon } from "lucide-react";
import Combobox, { type ComboboxOption } from "@/components/frontend/ui/form/inputs/combobox";
import Input from "@/components/frontend/ui/form/inputs/input";
import { Card, CardHeader, type CardVariant } from "@/components/frontend/ui/card";
import { cn } from "@/lib/utils";
import { buildCategoryOptions, filterDownloadSections, type DownloadOverviewSectionEntry } from "./data";
import { getRelationshipObject } from "@/lib/utils/theme-configuration-class";
import { DownloadItem } from "@/components/frontend/entity/download-item";

const gridCols = {
	"1": "grid-cols-1",
	"2": "grid-cols-1 md:grid-cols-2",
	"3": "grid-cols-1 md:grid-cols-2 lg:grid-cols-3",
	"4": "grid-cols-1 md:grid-cols-2 lg:grid-cols-4",
};

interface DownloadOverviewFiltersProps {
	sections: DownloadOverviewSectionEntry[];
	showSearchBar: boolean;
	searchPlaceholder: string;
	formVariantId: string;
	sectionColumns: string;
	columns: string;
	sectionTitleClass?: string;
	downloadCardVariant?: CardVariant;
	allCategoriesLabel: string;
	noResultsTitle: string;
	noResultsDescription: string;
}

export function DownloadOverviewFilters({
	sections,
	showSearchBar,
	searchPlaceholder,
	formVariantId,
	sectionColumns,
	columns,
	sectionTitleClass,
	downloadCardVariant,
	allCategoriesLabel,
	noResultsTitle,
	noResultsDescription,
}: DownloadOverviewFiltersProps) {
	const [query, setQuery] = useState("");
	const [category, setCategory] = useState("");

	const categoryOptions = useMemo(() => buildCategoryOptions(sections), [sections]);
	const showCategoryFilter = categoryOptions.length > 0;

	const categoryComboboxOptions = useMemo<ComboboxOption[]>(
		() => [{ value: "", label: allCategoriesLabel }, ...categoryOptions],
		[allCategoriesLabel, categoryOptions],
	);
	const categoryValue = useMemo<ComboboxOption | undefined>(
		() => categoryComboboxOptions.find((option) => option.value === category) ?? categoryComboboxOptions[0],
		[category, categoryComboboxOptions],
	);

	const filteredSections = useMemo(() => filterDownloadSections(sections, query, category), [sections, query, category]);

	return (
		<>
			{(showSearchBar || showCategoryFilter) && (
				<div
					className={cn(
						`ms-form-${formVariantId}`,
						"mb-8 md:mb-10 grid gap-3",
						showSearchBar && showCategoryFilter ? "md:grid-cols-[minmax(0,1fr)_16rem]" : "grid-cols-1",
					)}
				>
					{showSearchBar && (
						<Input type="text" name="q" value={query} onChange={(event) => setQuery(event.target.value)} placeholder={searchPlaceholder} />
					)}
					{showCategoryFilter && (
						<Combobox
							name="category"
							value={categoryValue}
							clearable={false}
							formVariant={formVariantId}
							options={categoryComboboxOptions}
							onValueChange={(value) => setCategory(value?.value ?? "")}
						/>
					)}
				</div>
			)}

			{filteredSections.length > 0 ? (
				<div className={cn("grid gap-x-10 gap-y-12", sectionColumns === "2" ? "md:grid-cols-2" : "grid-cols-1")}>
					{filteredSections.map((section, index) => (
						<section key={section.key ?? index} className="flex flex-col gap-6 ms-download-section">
							{(section.title.length > 0 || section.description.length > 0) && (
								<div className="flex flex-col gap-3">
									{section.title.length > 0 && <h2 className={sectionTitleClass}>{section.title}</h2>}
									{section.description.length > 0 && <p>{section.description}</p>}
								</div>
							)}

							<div className={cn("grid gap-4", gridCols[(columns ?? "3") as keyof typeof gridCols])}>
								{section.items.map((item) => (
									<DownloadItem key={item.id} cardVariant={getRelationshipObject(downloadCardVariant)} download={item} />
								))}
							</div>
						</section>
					))}
				</div>
			) : (
				<div className="py-12 text-center">
					<h3>{noResultsTitle}</h3>
					<p className="mt-2 text-sm text-muted-foreground">{noResultsDescription}</p>
				</div>
			)}
		</>
	);
}
